class PairingHistoryService {
  /** История переходов по турам. Проверка кэша не требует расчёта таблиц. */
  async buildFloatHistory(tournament) {
    const matches = tournament.isTeamTournament()
      ? tournament.getTeamGames(null, 'regular') : tournament.games.regular;
    const rounds = [...new Set(matches.map(match => match.round).filter(round => round > 0))].sort((a, b) => a - b);
    const cache = tournament._swissFloatHistory || {};
    const nextCache = {};
    const history = new Map();
    for (const round of rounds) {
      const pairs = matches.filter(match => match.round === round).map(match => ({
        side1Id: tournament.isTeamTournament() ? match.side1?.id : match.getSideParticipantId('white'),
        side2Id: tournament.isTeamTournament() ? match.side2?.id : match.getSideParticipantId('black'),
        isBye: match.isBye,
      }));
      const signature = this.floatSignature(tournament, round, pairs);
      const entry = cache[round]?.signature === signature ? cache[round] : {
        signature,
        transitions: this.floatTransitions(pairs, await tournament.calculateStandings(round - 1)),
      };
      nextCache[round] = entry;
      for (const { higher, lower } of entry.transitions) {
        for (const id of [higher, lower]) {
          if (!history.has(id)) history.set(id, { downfloatCount: 0, lastDownfloatRound: null, pairedUpDown: true });
        }
        history.get(higher).downfloatCount++;
        history.get(higher).lastDownfloatRound = round;
      }
    }
    this.storeFloatCache(tournament, nextCache);
    return history;
  }

  floatSignature(tournament, round, pairs) {
    return JSON.stringify([
      tournament.games.regular.filter(game => game.round < round),
      pairs.map(pair => JSON.stringify([pair.side1Id ?? null, pair.side2Id ?? null])).sort(),
    ]);
  }

  floatTransitions(pairs, standings) {
    const scores = new Map(standings.map(row => [row.id, Number(row.points)]));
    const transitions = [];
    for (const pair of pairs) {
      if (pair.isBye || !pair.side1Id || !pair.side2Id) continue;
      const first = scores.get(pair.side1Id);
      const second = scores.get(pair.side2Id);
      if (!Number.isFinite(first) || !Number.isFinite(second)) throw new Error('Missing historical Swiss pairing score');
      if (first !== second) transitions.push({
        higher: first > second ? pair.side1Id : pair.side2Id,
        lower: first > second ? pair.side2Id : pair.side1Id,
      });
    }
    return transitions;
  }

  storeFloatCache(tournament, value) {
    Object.defineProperty(tournament, '_swissFloatHistory', { configurable: true, writable: true, enumerable: false, value });
  }

  /** Запоминает новые переходы до записи партий; следующая загрузка сверит пары. */
  recordFloatPairings(tournament, round, pairs, standings) {
    const bySide = new Map(tournament.getActiveMatchSides().map(side => [side.id, side.meta?.participantId ?? side.participant?.id ?? side.id]));
    const normalized = pairs.map(pair => ({ ...pair,
      side1Id: bySide.get(pair.side1Id) ?? pair.side1Id,
      side2Id: bySide.get(pair.side2Id) ?? pair.side2Id,
    }));
    this.storeFloatCache(tournament, { ...tournament._swissFloatHistory, [round]: {
      signature: this.floatSignature(tournament, round, normalized),
      transitions: this.floatTransitions(normalized, standings),
    } });
  }

  /**
   * Строит историю игрока для жеребьёвки: соперники, цвета и bye.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} [from='regular']
   * @returns {Map<string, Object>}
   */
  buildPlayerHistory(tournament, from = 'regular') {
    const map = new Map();
    const participants = tournament.getActiveMatchSides();

    for (const participant of participants) {
      const currentPlayerId = participant?.entity?.id ?? participant?.id ?? null;
      const participantId = participant?.meta?.participantId ?? participant?.id ?? null;
      const participantPlayerIds = new Set(tournament.getParticipantEntityIds(participantId));
      const history = {
        opponents: new Set(),
        lastColors: [],
        whiteCount: 0,
        blackCount: 0,
        byeCount: 0,
      };
      const pastGames = tournament.getIndividualParticipantGames(participantId, from);

      for (const game of pastGames) {
        const whitePlayerId = game?.getSingleWhitePlayerId?.() ?? null;
        const blackPlayerId = game?.getSingleBlackPlayerId?.() ?? null;
        const isBye = game.isBye && (
          (Array.isArray(game.whitePlayerIds) && game.whitePlayerIds.some((playerId) => participantPlayerIds.has(playerId))) ||
          (Array.isArray(game.blackPlayerIds) && game.blackPlayerIds.some((playerId) => participantPlayerIds.has(playerId)))
        );

        if (!isBye) {
          const isWhite = game?.sideHasParticipant?.('white', participantId);
          const color = isWhite ? 'white' : 'black';
          const opponentParticipantId = isWhite
            ? game?.getSideParticipantId?.('black')
            : game?.getSideParticipantId?.('white');
          const opponentId = isWhite ? blackPlayerId : whitePlayerId;
          const opponentCurrentPlayerId = tournament.getParticipant(opponentParticipantId)?.entity?.id ?? opponentId;

          if (opponentCurrentPlayerId) history.opponents.add(opponentCurrentPlayerId);
          history.lastColors.push(color);
          if (color === 'white') history.whiteCount++;
          else history.blackCount++;
        } else {
          history.byeCount++;
        }
      }

      if (currentPlayerId) {
        map.set(currentPlayerId, history);
      }
    }

    return map;
  }

  /**
   * Строит историю player-participant для швейцарской жеребьёвки.
   * Ключи Map и opponents хранят participantId, потому что SwissPairingService
   * работает со строками standings, где row.id равен ID participant-слота.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} [from='regular']
   * @returns {Map<string, Object>}
   */
  buildPlayerParticipantHistory(tournament, from = 'regular') {
    const map = new Map();
    const participants = tournament.getActiveMatchSides();

    for (const participant of participants) {
      const participantId = participant?.meta?.participantId ?? participant?.id ?? null;
      if (!participantId) continue;

      const participantPlayerIds = new Set(tournament.getParticipantEntityIds(participantId));
      const history = {
        opponents: new Set(),
        lastColors: [],
        whiteCount: 0,
        blackCount: 0,
        byeCount: 0,
      };
      const pastGames = tournament.getIndividualParticipantGames(participantId, from);

      for (const game of pastGames) {
        const isBye = game.isBye && (
          (Array.isArray(game.whitePlayerIds) && game.whitePlayerIds.some((playerId) => participantPlayerIds.has(playerId))) ||
          (Array.isArray(game.blackPlayerIds) && game.blackPlayerIds.some((playerId) => participantPlayerIds.has(playerId)))
        );

        if (isBye) {
          history.byeCount++;
          continue;
        }

        const isWhite = game?.sideHasParticipant?.('white', participantId);
        const color = isWhite ? 'white' : 'black';
        const opponentParticipantId = isWhite
          ? game?.getSideParticipantId?.('black')
          : game?.getSideParticipantId?.('white');

        if (opponentParticipantId) history.opponents.add(opponentParticipantId);
        history.lastColors.push(color);
        if (color === 'white') history.whiteCount++;
        else history.blackCount++;
      }

      map.set(participantId, history);
    }

    return map;
  }

  /**
   * Строит историю команд для жеребьёвки: соперники, цвета сторон и bye.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} [from='regular']
   * @returns {Map<string, Object>}
   */
  buildTeamHistory(tournament, from = 'regular') {
    const map = new Map();
    const useColors = tournament.shouldUsePairingColors();
    const participants = tournament.getActiveMatchSides();

    for (const participant of participants) {
      const history = {
        opponents: new Set(),
        lastColors: [],
        whiteCount: 0,
        blackCount: 0,
        byeCount: 0,
      };
      const pastTeamMatches = tournament.getTeamGames(participant.id, from);

      for (const teamMatch of pastTeamMatches) {
        const side1Id = teamMatch?.side1?.id ?? null;
        const side2Id = teamMatch?.side2?.id ?? null;
        const isBye = !side1Id || !side2Id;

        if (isBye) {
          history.byeCount++;
          continue;
        }

        const isSide1 = side1Id === participant.id;
        const opponentId = isSide1 ? side2Id : side1Id;
        if (opponentId) history.opponents.add(opponentId);

        if (useColors) {
          const color = isSide1 ? 'white' : 'black';
          history.lastColors.push(color);
          if (color === 'white') history.whiteCount++;
          else history.blackCount++;
        }
      }

      map.set(participant.id, history);
    }

    return map;
  }

  /**
   * Строит историю участников текущего формата турнира.
   * @param {import('@/models/Tournament').default} tournament
   * @param {string} [from='regular']
   * @returns {Map<string, Object>}
   */
  buildParticipantHistory(tournament, from = 'regular') {
    if (tournament.isTeamTournament()) {
      return this.buildTeamHistory(tournament, from);
    }

    return this.buildPlayerParticipantHistory(tournament, from);
  }
}

export default new PairingHistoryService();
